/**
 * @file        src/lib/statusTone.ts
 * @description The three verdict tones of the site — «норма», «внимание»,
 *              «проблема» — and the Tailwind classes each one paints with.
 *              Introduced by series inspection-cases (step 01) for the report
 *              examples of the inspection block; meant for every future place
 *              that shows a check result (Encar verdict, auction sheet flags).
 * @universal   statusTone — the ONE mapping from a verdict to its colours.
 *              Registered in knowledge/universals/components.md.
 *              Accepts: a StatusTone key; returns ready class strings.
 * @rule        COLOURS COME ONLY FROM THE THEME TOKENS `success` / `warning` /
 *              `danger` (+ `-soft`) in globals.css. No `green-600`, no hex here:
 *              the tokens are the design decision, this file only names roles.
 * @rule        CLASS STRINGS ARE WRITTEN OUT IN FULL. Tailwind finds classes by
 *              scanning source text, so a class built from pieces
 *              (`bg-${tone}`) is never generated and silently renders nothing.
 * @rule        COLOUR IS NEVER THE ONLY SIGNAL. Every consumer pairs the tone
 *              with a word or an icon, so the verdict survives colour blindness
 *              and a screen reader.
 */

/** The three verdicts a check can end with. */
export type StatusTone = "ok" | "warn" | "bad";

/** Class roles one tone paints. */
export interface ToneClasses {
  /** Small solid dot, e.g. in a tab. */
  dot: string;
  /** Solid pill with a short verdict word. */
  pill: string;
  /** Soft background for a verdict strip. */
  soft: string;
  /** Solid round icon badge (check / exclamation / cross). */
  icon: string;
  /** Coloured text or stroke. */
  text: string;
  /** Soft chip for a highlighted item in a list. */
  chip: string;
  /** SVG fill for a shape (drawings such as the damage map). */
  fill: string;
}

/**
 * Tone → classes.
 *
 * @important The `warn` pill and icon use white text on `warning` (#B45309):
 *            contrast 5.0:1, the same AA level as the other two.
 */
export const TONE_CLASSES: Record<StatusTone, ToneClasses> = {
  ok: {
    dot: "bg-success",
    pill: "bg-success text-white",
    soft: "bg-success-soft",
    icon: "bg-success text-white",
    text: "text-success",
    chip: "bg-success-soft text-success",
    fill: "fill-success",
  },
  warn: {
    dot: "bg-warning",
    pill: "bg-warning text-white",
    soft: "bg-warning-soft",
    icon: "bg-warning text-white",
    text: "text-warning",
    chip: "bg-warning-soft text-warning",
    fill: "fill-warning",
  },
  bad: {
    dot: "bg-danger",
    pill: "bg-danger text-white",
    soft: "bg-danger-soft",
    icon: "bg-danger text-white",
    text: "text-danger",
    chip: "bg-danger-soft text-danger",
    fill: "fill-danger",
  },
};

/** Screen-reader word for each tone — read before the item it marks. */
export const TONE_SR_LABEL: Record<StatusTone, string> = {
  ok: "норма",
  warn: "внимание",
  bad: "проблема",
};
